'use client';

import { useState, useEffect, useMemo } from 'react';
import { Chip, Box, Typography } from '@mui/material';
import DataTable from '../../../../components/DataTable';
import ActionMenu from '../../../../components/ActionMenu';
import { useNotifier } from '../../../../components/Notifier';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';

import SpecificationAddEdit from './SpecificationAddEdit';
import { useLanguage } from '../../../../utils/i18n';
import { useTireSpecDataList, deleteTireSpec, getTireSpecList } from '../../../../stores/tireSpecStore';
import { useCompanyList } from '../../../../stores/companyStore';

function SpecificationPageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'spec_name', label: t('specification.spec_name', 'Spec Name') },
      { value: 'tire_brand', label: t('specification.brand', 'Brand') },
      { value: 'tire_size', label: t('specification.size', 'Size') },
      { value: 'pattern', label: t('specification.pattern', 'Pattern') },
      { value: 'original_tread_depth', label: t('specification.originalTreadDepth', 'OTD Standard (mm)') },
      { value: 'pressure_std', label: t('specification.pressureStd', 'Air Pressure Standard (psi)') },
      { value: 'status', label: t('masterCompany.status', 'Status') }
    ],
    [t]
  );

  const { companyOptions } = useCompanyList();

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const handleCompanyChange = (options) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('error saving company to local storage');
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading,
    refetch
  } = useTireSpecDataList(paginationModel.page, paginationModel.pageSize, selectedCompanies, searchField, searchValue);

  const handleFetchAllForExport = async () => {
    return await getTireSpecList();
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteTireSpec(confirmTargetId);
      notify({ message: t('specification.notifSuccess', 'Specification successfully deleted'), severity: 'success' });
      refetch();
    } catch (err) {
      console.error(err);
      notify({ message: t('specification.notifFailed', 'Failed to delete Specification'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'company_name',
      headerName: t('general.company', 'Company'),
      flex: 1,
      valueGetter: (params, row) => row?.join?.company_name || row?.company_name || '-'
    },
    { field: 'spec_name', headerName: t('specification.spec_name', 'Spec Name'), flex: 1 },
    // {
    //   field: 'brand_size',
    //   headerName: t('specification.brand_size', 'Brand & Size'),
    //   flex: 1.5,
    //   renderCell: (params) => (
    //     <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
    //       <Typography variant="body2">{params.row?.join?.tire_brand_name || params.row?.tire_brand_name || '-'}</Typography>
    //       <Typography
    //         variant="caption"
    //         color="text.secondary"
    //         sx={{ display: 'block', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
    //       >
    //         {params.row?.join?.tire_size_code ||
    //           params.row?.join?.tire_size_code_internal ||
    //           params.row?.tire_size_code ||
    //           params.row?.tire_size_code_internal ||
    //           '-'}
    //       </Typography>
    //     </Box>
    //   )
    // },
    {
      field: 'tire_brand',
      headerName: t('specification.brand', 'Brand'),
      flex: 1,
      valueGetter: (paramsOrValue, rowOrNull) => {
        const row = rowOrNull || paramsOrValue?.row;
        return row?.join?.tire_brand_name || row?.tire_brand_name || '-';
      },
      renderCell: (params) => {
        const row = params.row;
        const main = row?.join?.tire_brand_name || row?.tire_brand_name || '-';
        const secondary = row?.join?.tire_brand_code_internal || row?.join?.tire_brand_code || '-';
        return (
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
            <Typography variant="body2">{main}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {secondary}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'tire_size',
      headerName: t('specification.size', 'Size'),
      flex: 1,
      // hide: true,
      valueGetter: (paramsOrValue, rowOrNull) => {
        const row = rowOrNull || paramsOrValue?.row;
        return (
          row?.join?.tire_size_code_internal || row?.join?.tire_size_code || row?.tire_size_code_internal || row?.tire_size_code || '-'
        );
      }
    },
    { field: 'pattern', headerName: t('specification.pattern', 'Pattern'), flex: 1 },
    { field: 'original_tread_depth', headerName: t('specification.originalTreadDepth', 'OTD Standard (mm)'), flex: 1 },
    { field: 'min_tread_depth', headerName: t('tirePerformance.minTreadDepth', 'Min Tread Depth (mm)'), flex: 1, hide: true },
    { field: 'pressure_std', headerName: t('specification.pressureStd', 'Air Pressure Standard (psi)'), flex: 1 },
    {
      field: 'status',
      headerName: t('masterCompany.status', 'Status'),
      flex: 0.8,
      renderCell: (params) => {
        const isActive = params.row.status === 'Active' || params.row.status === true || params.row.status === 1;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'default'}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: isActive ? 'success.light' : 'action.disabledBackground',
              color: isActive ? 'success.dark' : 'text.disabled'
            }}
          />
        );
      }
    },
    { field: 'load_index', headerName: t('specification.loadIndex', 'Load Index'), flex: 1, hide: true },
    { field: 'tkph', headerName: t('specification.tkphTmph', 'TKPH'), flex: 1, hide: true },
    { field: 'expected_lifetime_km', headerName: t('specification.expectedLifetimeKm', 'Expected Lifetime (KM)'), flex: 1, hide: true },
    {
      field: 'expected_lifetime_hour',
      headerName: t('specification.expectedLifetimeHour', 'Expected Lifetime (Hour)'),
      flex: 1,
      hide: true
    },
    { field: 'max_speed', headerName: t('specification.maxSpeed', 'Max Speed'), flex: 1, hide: true },
    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.pattern)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('specification.title', 'Master Specification')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('specification.search', 'Search Specification')}
        hideCompanyFilter={false}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        rowHeight={90}
      />

      {openDialog && <SpecificationAddEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={() => refetch()} />}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function SpecificationPage() {
  return <SpecificationPageContent />;
}
